import React, { useState, useRef } from 'react';
import { CONTACT_EMAIL } from '../lib/roles';

/*
 * Job application form.
 *
 * Separate endpoint from the site assessment form on purpose: student
 * applications and operator leads want different notification settings, and
 * mixing them means one submission quota and one inbox stream for two entirely
 * different conversations. Set REACT_APP_CAREERS_ENDPOINT to a Formspree form
 * created for hiring — see .env.example.
 *
 * ── Why this POSTs multipart and the assessment form POSTs JSON ────────────
 *
 * A resume is a file, and JSON cannot carry one. So this sends FormData, which
 * also means we must NOT set Content-Type: the browser has to write it itself
 * to include the multipart boundary. Setting it by hand produces a request that
 * looks valid and arrives with no file attached.
 *
 * File uploads are a paid Formspree feature (Personal plan and up). On the free
 * plan the submission is rejected, and the error state below sends the
 * applicant to email rather than swallowing their application.
 *
 * With no endpoint configured at all, submitting opens the visitor's mail
 * client with every answer pre-filled. The resume cannot ride along in a
 * mailto, so that path asks them to attach it. Never a dead button.
 */

const CAREERS_ENDPOINT = process.env.REACT_APP_CAREERS_ENDPOINT || '';
/* Web3Forms takes its key as a form field; harmless for Formspree and Basin. */
const FORM_ACCESS_KEY = process.env.REACT_APP_FORM_ACCESS_KEY || '';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* Formspree's own ceiling is 25 MB. This is lower on purpose: a resume that
   doesn't fit in 10 MB is a resume with a problem, and failing here with a
   clear message beats failing at the endpoint with a 413. */
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = '.pdf,.doc,.docx';
const ACCEPT_RE = /\.(pdf|docx?)$/i;

const TEXT_FIELDS = ['name', 'email', 'school', 'start', 'hands_on'];

function ApplicationForm({ role }) {
  const [values, setValues] = useState({
    name: '',
    email: '',
    school: '',
    start: '',
    hands_on: '',
  });
  const [resume, setResume] = useState(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const honeypot = useRef(null);
  const fileInput = useRef(null);

  const set = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  function onFile(e) {
    const file = e.target.files && e.target.files[0];
    setResume(file || null);
    setErrors((prev) => ({ ...prev, resume: undefined }));
  }

  function validate() {
    const next = {};
    if (!values.name.trim()) next.name = 'Please add your name.';
    if (!values.email.trim()) next.email = 'Please add an email address.';
    else if (!EMAIL_RE.test(values.email.trim()))
      next.email = 'That doesn’t look like an email address.';
    if (!values.school.trim()) next.school = 'School and year, please.';
    if (!values.start.trim()) next.start = 'When could you start?';
    if (!values.hands_on.trim()) next.hands_on = 'This one we do read. A few sentences is plenty.';

    if (!resume) next.resume = 'Please attach a resume.';
    else if (!ACCEPT_RE.test(resume.name)) next.resume = 'PDF, DOC, or DOCX, please.';
    else if (resume.size > MAX_BYTES) next.resume = 'That file is over 10MB. Send a smaller one.';

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function mailtoFallback() {
    const body = [
      `Role: ${role.title}`,
      `Name: ${values.name}`,
      `Email: ${values.email}`,
      `School and year: ${values.school}`,
      `Available start date: ${values.start}`,
      '',
      'Most hands-on thing built, measured, or fixed:',
      values.hands_on,
      '',
      '--',
      'Please attach your resume to this email before sending.',
    ].join('\n');
    window.location.href =
      `mailto:${CONTACT_EMAIL}` +
      `?subject=${encodeURIComponent(`Application: ${role.title}`)}` +
      `&body=${encodeURIComponent(body)}`;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (status === 'sending') return;

    // Bots fill every field they find; humans never see this one.
    if (honeypot.current && honeypot.current.value) return;

    if (!validate()) {
      // Move focus to the first problem so keyboard and screen-reader users
      // aren't left guessing why nothing happened.
      const first = TEXT_FIELDS.find((f) => !values[f].trim());
      const el = first
        ? document.getElementById(`apply-${first}`)
        : fileInput.current;
      if (el && el.focus) el.focus();
      return;
    }

    if (!CAREERS_ENDPOINT) {
      mailtoFallback();
      return;
    }

    setStatus('sending');
    try {
      const data = new FormData();
      if (FORM_ACCESS_KEY) data.append('access_key', FORM_ACCESS_KEY);
      data.append('_subject', `Application: ${role.title} (${values.name.trim()})`);
      data.append('role', role.title);
      data.append('name', values.name.trim());
      data.append('email', values.email.trim());
      data.append('school_and_year', values.school.trim());
      data.append('available_start_date', values.start.trim());
      data.append('most_hands_on_thing', values.hands_on.trim());
      data.append('resume', resume, resume.name);

      // No Content-Type header: the browser writes it, with the boundary.
      const res = await fetch(CAREERS_ENDPOINT, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: data,
      });
      if (!res.ok) throw new Error(`Careers endpoint returned ${res.status}`);
      setStatus('sent');
    } catch (err) {
      console.error('[careers] submit failed:', err);
      setStatus('error');
    }
  }

  if (status === 'sent') {
    return (
      <section className="role-apply" id="apply">
        <h2 className="role-apply-title">Apply</h2>
        <div className="apply-done" role="status">
          <p className="apply-done-title">Thanks, {values.name.trim().split(' ')[0]}. We’ve got it.</p>
          <p className="apply-done-body">
            A person reads every one of these. If it looks like a fit we’ll write
            back to set up a short call, and then a visit to the lab so you can
            see the rigs before you commit your evenings to them.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="role-apply" id="apply">
      <h2 className="role-apply-title">Apply</h2>
      <p className="role-apply-lead">
        Six fields. What you’ve built matters more to us than how the resume is
        formatted.
      </p>

      <form className="form apply-form" onSubmit={handleSubmit} noValidate>
        <div className="form-row">
          <div className="field">
            <label className="field-label" htmlFor="apply-name">
              Name
            </label>
            <input
              id="apply-name"
              className={`field-input${errors.name ? ' is-error' : ''}`}
              type="text"
              name="name"
              autoComplete="name"
              value={values.name}
              onChange={set('name')}
              aria-invalid={Boolean(errors.name)}
              aria-describedby={errors.name ? 'apply-name-err' : undefined}
            />
            {errors.name && (
              <p className="field-error" id="apply-name-err">
                {errors.name}
              </p>
            )}
          </div>

          <div className="field">
            <label className="field-label" htmlFor="apply-email">
              Email
            </label>
            <input
              id="apply-email"
              className={`field-input${errors.email ? ' is-error' : ''}`}
              type="email"
              name="email"
              autoComplete="email"
              value={values.email}
              onChange={set('email')}
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? 'apply-email-err' : undefined}
            />
            {errors.email && (
              <p className="field-error" id="apply-email-err">
                {errors.email}
              </p>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="field">
            <label className="field-label" htmlFor="apply-school">
              School and year
            </label>
            <input
              id="apply-school"
              className={`field-input${errors.school ? ' is-error' : ''}`}
              type="text"
              name="school_and_year"
              placeholder="NYU, junior, mechanical engineering"
              value={values.school}
              onChange={set('school')}
              aria-invalid={Boolean(errors.school)}
              aria-describedby={errors.school ? 'apply-school-err' : undefined}
            />
            {errors.school && (
              <p className="field-error" id="apply-school-err">
                {errors.school}
              </p>
            )}
          </div>

          <div className="field">
            <label className="field-label" htmlFor="apply-start">
              Available start date
            </label>
            {/* Text, not a date picker: "right away" and "after finals" are
                real answers and a date input can't hold either. */}
            <input
              id="apply-start"
              className={`field-input${errors.start ? ' is-error' : ''}`}
              type="text"
              name="available_start_date"
              placeholder="Right away, or Sept 8"
              value={values.start}
              onChange={set('start')}
              aria-invalid={Boolean(errors.start)}
              aria-describedby={errors.start ? 'apply-start-err' : undefined}
            />
            {errors.start && (
              <p className="field-error" id="apply-start-err">
                {errors.start}
              </p>
            )}
          </div>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="apply-hands_on">
            What’s the most hands-on thing you’ve built, measured, or fixed?
          </label>
          <textarea
            id="apply-hands_on"
            className={`field-input field-textarea${errors.hands_on ? ' is-error' : ''}`}
            name="most_hands_on_thing"
            rows={5}
            placeholder="A few sentences. What went wrong with it is the interesting part."
            value={values.hands_on}
            onChange={set('hands_on')}
            aria-invalid={Boolean(errors.hands_on)}
            aria-describedby={errors.hands_on ? 'apply-hands_on-err' : undefined}
          />
          {errors.hands_on && (
            <p className="field-error" id="apply-hands_on-err">
              {errors.hands_on}
            </p>
          )}
        </div>

        <div className="field">
          <span className="field-label" id="apply-resume-label">
            Resume
          </span>
          {/* The native control is hidden rather than restyled: file inputs
              can't be styled across browsers, and the label is the accessible
              trigger for it. */}
          <div className="file-field">
            <label className="file-button" htmlFor="apply-resume">
              Choose file
            </label>
            <input
              ref={fileInput}
              id="apply-resume"
              className="sr-only"
              type="file"
              name="resume"
              accept={ACCEPT}
              onChange={onFile}
              aria-invalid={Boolean(errors.resume)}
              aria-describedby="apply-resume-hint"
            />
            <span className={`file-name${resume ? ' is-set' : ''}`}>
              {resume ? resume.name : 'No file chosen'}
            </span>
          </div>
          <p className="field-hint" id="apply-resume-hint">
            PDF, DOC, or DOCX, up to 10MB.
          </p>
          {errors.resume && <p className="field-error">{errors.resume}</p>}
        </div>

        {/* Honeypot — off-screen, not display:none, so bots still fill it. */}
        <input
          ref={honeypot}
          type="text"
          name="_gotcha"
          className="sr-only"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn--primary form-submit"
            disabled={status === 'sending'}
          >
            {status === 'sending' ? 'Sending…' : 'Send application'}
          </button>
          <p className="form-note">
            We read every one and reply either way.
          </p>
        </div>

        {status === 'error' && (
          <p className="form-alert" role="alert">
            That didn’t go through. Email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with your
            resume attached and we’ll pick it up from there.
          </p>
        )}
      </form>
    </section>
  );
}

export default ApplicationForm;
